import { ShoppingCart, TrendingUp, Target, Users, BarChart3, Repeat, Megaphone, Mail, LineChart, Store } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "System-Driven Customer Acquisition",
    icon: Target,
    components: [
      "Channel diversification across Meta, Google, TikTok",
      "High-intent traffic capture",
      "Budget efficiency controls",
    ],
    outcome: "Lower CAC with predictable scale.",
  },
  {
    name: "Conversion System Engineering",
    icon: TrendingUp,
    components: [
      "Landing page and PDP optimization",
      "Checkout funnel restructuring",
      "Personalization and A/B testing",
    ],
    outcome: "Higher conversion rates and AOV.",
  },
  {
    name: "Retention & LTV Architecture",
    icon: Repeat,
    components: [
      "Lifecycle marketing automation",
      "Loyalty and subscription systems",
      "Churn prediction and prevention",
    ],
    outcome: "Maximize customer lifetime value.",
  },
  {
    name: "Revenue Attribution System",
    icon: BarChart3,
    components: [
      "Multi-touch attribution modeling",
      "Customer journey mapping",
      "Cohort analysis and forecasting",
    ],
    outcome: "Clear visibility into what drives revenue.",
  },
  {
    name: "Marketplace Growth System",
    icon: ShoppingCart,
    components: [
      "Amazon and retail media strategy",
      "Listing optimization frameworks",
      "Inventory and pricing intelligence",
    ],
    outcome: "Capture demand across all channels.",
  },
  {
    name: "Audience Development System",
    icon: Users,
    components: [
      "First-party data strategy",
      "Lookalike and predictive audiences",
      "Cross-channel audience orchestration",
    ],
    outcome: "Build scalable audience assets.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Diagnose Growth Constraints",
    description: "Deep analysis of current performance across acquisition, conversion, and retention to identify systemic blockers.",
  },
  {
    number: "02",
    title: "Engineer Growth Systems",
    description: "Design integrated acquisition, conversion, and retention systems with clear ownership and success metrics.",
  },
  {
    number: "03",
    title: "Integrate Analytics for Clarity",
    description: "Build attribution and measurement infrastructure to enable confident, data-driven decisions.",
  },
  {
    number: "04",
    title: "Optimize Against Revenue Efficiency",
    description: "Continuous iteration focused on contribution margin and long-term profitability, not vanity metrics.",
  },
];

const technologyCategories = [
  { title: "Advertising", icon: Megaphone, items: ["Meta Ads", "Google Ads", "TikTok Ads", "Amazon Ads"] },
  { title: "Email & SMS", icon: Mail, items: ["Klaviyo", "Attentive", "Mailchimp", "Postscript"] },
  { title: "Analytics", icon: LineChart, items: ["GA4", "Triple Whale", "Northbeam", "Polar Analytics"] },
  { title: "Platforms", icon: Store, items: ["Shopify Plus", "BigCommerce", "WooCommerce", "Magento"] },
];

const faqs = [
  {
    question: "What types of e-commerce businesses do you work with?",
    answer: "We work with DTC brands, omnichannel retailers, and marketplace sellers. Our focus is brands doing ₹5Cr+ / $1M+ in revenue looking to scale profitably through systems, not campaigns.",
  },
  {
    question: "How do you approach customer acquisition in a post-iOS14 world?",
    answer: "We focus on first-party data, server-side tracking, and multi-touch attribution. We also diversify acquisition across platforms to reduce dependency on any single channel.",
  },
  {
    question: "How do you balance growth and profitability?",
    answer: "We set clear CAC and LTV targets upfront and optimize toward contribution margin, not just ROAS. Growth without profitability isn't sustainable growth.",
  },
  {
    question: "How long before we see results?",
    answer: "Most clients see measurable improvements within 60-90 days. However, building sustainable growth systems takes 6-12 months of consistent optimization.",
  },
];

const qualification = {
  forYou: [
    "Brands doing ₹5Cr+ / $1M+ annual revenue",
    "Teams with ongoing paid or organic traffic",
    "Businesses focused on sustainable profitability",
    "Leaders ready to invest in systems, not quick fixes",
  ],
  notForYou: [
    "Early-stage validation experiments",
    "One-off campaign execution needs",
    "Brands seeking short-term traffic spikes",
    "Teams not ready for measurement and iteration",
  ],
};

const EcommerceGrowth = () => {
  return (
    <SubServicePageLayout
      seoTitle="E-commerce Growth Services"
      seoDescription="End-to-end growth systems for online retailers. From customer acquisition to retention optimization. Lower CAC, higher AOV, improved LTV."
      canonicalUrl="https://xperfomax.com/solutions/ecommerce-growth"
      category="solutions"
      categoryLabel="Solutions / E-commerce Growth"
      headline="E-commerce growth, engineered."
      subheadline="We help online retailers scale profitably through integrated customer acquisition, conversion optimization, and retention strategies."
      heroSecondaryText="Built for brands scaling beyond channel-led growth into predictable, profitable systems."
      problemTitle="Why most e-commerce growth stalls."
      problems={[
        "Rising acquisition costs erode margins faster than revenue scales.",
        "Channel-dependent growth creates instability and forecasting risk.",
        "Retention and repeat purchase are under-engineered.",
        "Attribution gaps lead to inefficient budget allocation.",
      ]}
      solutionTitle="What this solution solves."
      solutionPoints={[
        "Builds a balanced acquisition, conversion, and retention system",
        "Improves revenue efficiency, not just traffic volume",
        "Reduces dependence on any single growth channel",
        "Aligns marketing decisions with profitability metrics",
      ]}
      systemTitle="A complete growth system for e-commerce."
      systemDescription="Every component works together to drive sustainable, profitable growth."
      services={services}
      processTitle="How growth is engineered."
      processSteps={processSteps}
      technologyCategories={technologyCategories}
      techTitle="Platforms"
      techDescription="We're platform-agnostic and work with the tools that best serve your business."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready to engineer sustainable ecommerce growth?"
      finalCtaDescription="Let's discuss how we can build a growth system that delivers sustainable results."
      finalCtaButtonText="Discuss your ecommerce challenge"
    />
  );
};

export default EcommerceGrowth;
